import React, {useImperativeHandle, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import constants from 'helpers/constants';
import {useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import StyledChooseSaveWizardContent
  from 'components/organisms/WizardContent/ChooseSaveWizardContent/ChooseSaveWizardContent.styles';
import utils from 'helpers/utils';

const ChooseSaveWizardContent = React.forwardRef((props, ref) => {
  const {
    wizard,
    step,
    tableProvider,
    saveTypes,
    ...innerProps
  } = useComponentProps(props, 'ChooseSaveWizardContent');

  const innerRef = useRef(null);
  const setDataEvent = useEffectEvent(wizard.setData);

  useImperativeHandle(ref, () => innerRef.current);

  const authorize = useAuthorize();

  const saveOptions = useMemo(() => {
    const saveOptions = utils.clone(constants.data.entitySaveTypes
      .filter((st) => st.enabled)
      .filter((st) => !st.companies || tableProvider.list?.meta?.resultsCount > 0)
      .filter((st) => !utils.isDefined(st.userView) || Boolean(tableProvider.view?.userView) === st.userView)
      .filter((st) => !saveTypes || saveTypes.find((a) => a.value === st.value))
      .filter((st) => authorize({attribute: `database.entity.save.${st.value}`})), true);

    saveOptions.forEach((st) => {
      if (st.description.includes('{{companies}}')) {
        const resultsCount = Math.min(tableProvider.listSelection?.max, tableProvider.list?.meta?.resultsCount);
        const resultsCountOver = tableProvider.list?.meta?.resultsCount > tableProvider.listSelection?.max;
        const companies = resultsCountOver ?
          `first ${utils.formatNumber(resultsCount)} compan${resultsCount === 1 ? 'y' : 'ies'}` :
          `${utils.formatNumber(resultsCount)} compan${resultsCount === 1 ? 'y' : 'ies'}`;

        st.description = st.description.replace('{{companies}}', companies);
      }
    });

    return saveOptions;
  }, [saveTypes, authorize, tableProvider.listSelection?.max, tableProvider.list?.meta?.resultsCount, tableProvider.view?.userView]);

  const fields = useMemo(() => ([{
    name: 'type',
    label: 'How would you like to save the search?',
    inlineLabel: 'Save type',
    entity: 'saveSearch',
    type: constants.formFieldTypes.category,
    conversion: constants.formFieldTypes.none,
    options: saveOptions,
    required: true,
    FormFieldProps: {
      multiple: false,
      limitOptions: false,
      hiddenIcons: false,
      readOnlyChip: false,
      IconProps: {
        color: 'primary'
      },
      ListProps: {
        orientation: 'vertical',
        gap: 8
      },
      CategoryCardProps: {
        variant: 'inline',
        size: 'large',
        showSelected: true
      }
    }
  }]), [saveOptions]);

  const handleChange = (field, value) => {
    setDataEvent?.((current) => ({
      ...current,
      [wizard.dataKey]: {
        ...current[wizard.dataKey],
        type: value
      }
    }));
  }

  const handleSubmit = (values) => {
    setDataEvent?.((current) => ({
      ...current,
      [wizard.dataKey]: {
        ...current[wizard.dataKey],
        type: values['type']
      }
    }));
  }

  return <StyledChooseSaveWizardContent ref={innerRef} {...innerProps}
                                        wizard={wizard}
                                        step={step}
                                        fields={fields}
                                        onChange={handleChange}
                                        onSubmit={handleSubmit}
                                        InlineFormProps={{autoFocus: false}}/>
});

ChooseSaveWizardContent.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  wizard: PropTypes.object,
  step: PropTypes.object,
  tableProvider: PropTypes.object,
  saveTypes: PropTypes.array
};

export default ChooseSaveWizardContent;
